<template>
  <div v-if="post" :class="$style.container" v-viewer="{ rotatable: false, title: false }">
    <div v-if="topImage" :class="$style.imageWrap">
      <img
        :src="topImage" alt="blog header image"
        :class="$style.topImage"
      >
    </div>
    <div :class="$style.header">
      <Category v-if="post.category" :category="post.category" :class="$style.category" />
      <div :class="$style.date">{{ date }}</div>
    </div>
    <div v-if="post.author" :class="$style.author">
      <i>{{ $t('post.author') }}: {{ post.author }}</i>
    </div>
    <div :class="$style.title">{{ post.title }}</div>
    <div>{{ post.subtitle }}</div>
    <p :class="$style.text" v-html="text"></p>
    <tabs
      :tabs="tabs"
      :currentTab="currentTab"
      :class="$style.tabs"
      @onClick="newTab => currentTab = newTab"
    />
    <div :class="$style.bottomMedia">
      <template v-if="currentTab === 'photos'">
        <img
          v-for="(src, idx) in bottomImages" :key="'image' + idx"
          :src="src"
          :alt="`blog footer image ${idx + 1}`"
          :class="$style.bottomMediaItem"
        >
      </template>
      <template v-else-if="currentTab === 'videos'">
        <!-- <video
          v-for="(src, idx) in bottomVideos" :key="'video' + idx"
          :src="src"
          preload="metadata"
          :class="$style.bottomMediaItem"
          @click="openVideo(src)"
        ></video> -->
        <MediaPreview
          v-for="(src, idx) in bottomVideos" :key="'video' + idx"
          :src="src"
          isVideo
          :class="$style.bottomMediaItem"
          :isCloseBtn="false"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { formatDistanceStrict, format } from '@/utils/date'
import { isToday } from 'date-fns'
import Category from '@/components/Category'
import api from '@/api'
import linkifyUrls from 'linkify-urls'
import Tabs from 'vue-tabs-with-active-line'
import MediaPreview from '@/components/MediaPreview'

export default {
  name: 'blog-post-view',
  components: {
    Category,
    Tabs,
    MediaPreview
  },
  props: {
    blogId: {
      required: true,
      type: String
    },
    postId: {
      required: true,
      type: String
    }
  },
  data () {
    return {
      currentTab: 'photos',
      tabs: [
        {
          title: this.$t('post.tabPhotos'),
          value: 'photos'
        },
        {
          title: this.$t('post.tabVideos'),
          value: 'videos'
        }
      ]
    }
  },
  async created () {
    await this.fetchBlog(this.blogId)
    await this.fetchPost({
      blogUuid: this.blog.uuid,
      postUuid: this.postId
    })
    await this.registerPostView()
  },
  computed: {
    ...mapGetters({
      getPost: 'entities/posts/getPost',
      blogById: 'entities/blogs/getBlog' // 'entities/blogs/find'
    }),
    blog () {
      return this.blogById(this.blogId)
    },
    post () {
      return this.getPost(this.postId)
    },
    date () {
      const date = new Date(this.post.createdAt)
      return isToday(date) ? formatDistanceStrict(date, new Date()) : format(date)
    },
    topImage () {
      const imgObj = this.post.images.find(obj => obj.position === 1)
      if (!imgObj) return
      return imgObj.image
    },
    bottomImages () {
      return this.post.images
        .filter(obj => obj.position === 2)
        .sort((a, b) => a.order - b.order)
        .map(obj => obj.image)
    },
    bottomVideos () {
      return this.post.videos
        .filter(obj => obj.position === 2)
        .sort((a, b) => a.order - b.order)
        .map(obj => obj.video)
    },
    text () {
      let escapedText = this.post.text
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
      return linkifyUrls(escapedText, {
        attributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          onclick: 'event.stopPropagation()'
        }
      })
    }
  },
  methods: {
    ...mapActions({
      fetchPost: 'entities/posts/fetchPost',
      fetchBlog: 'entities/blogs/fetchByShortId'
    }),
    registerPostView () {
      return api.blogs.sendPostView({
        postUuid: this.postId
      })
    }
  }
}
</script>

<style module lang="scss">
.container {
  padding: 20px;
  overflow-x: hidden;
  overflow-y: scroll;
}
.header {
  margin-bottom: 4px;
}
.author {
  margin-bottom: 10px;
}
.imageWrap {
  text-align: center;
}
.topImage {
  margin: 0 auto;
  max-height: 35vh;
  object-fit: contain;
  cursor: pointer;
}
.header {
  display: flex;
  justify-content: space-between;
}
.title {
  text-transform: uppercase;
  font-size: 22px;
  font-weight: 500;
}
.text {
  margin-top: 12px;
  &:first-letter {
    font-size: 40px;
    font-weight: 500;
  }
}
.tabs:global(.tabs) {
  margin-bottom: 12px;
  // :global(.tabs__item) {
  //   flex-grow: unset;
  //   flex-basis: auto;
  // }
}
.bottomMedia {
  display: flex;
  margin-top: 16px;
  overflow-x: scroll;
}
.bottomMediaItem,
.bottomMediaItem:global(.gl-media-preview) {
  max-width: 33%;
  max-height: 300px;
  height: auto;
  object-fit: contain;
  cursor: pointer;
  margin-right: 8px;
}
</style>
